/**
 * 统一扫码 Hook（Issue #186）。
 *
 * 红线：
 * - 必须通过 `@jinzhuan/api-client` typed client 调用后端 `/api/v1/scan/resolve`；
 *   禁止手写 fetch、axios 或纯前端假状态。
 * - 后端会对任何扫码尝试写一条 `scan_events` 审计；前端不做白名单 / 黑名单。
 * - 失败结果（invalid_format / not_found / signature_mismatch / expired /
 *   revoked / duplicate / business_type_mismatch …）必须如实展示给操作人，
 *   不允许在前端伪装为成功。
 */
import { useCallback, useState } from 'react';
import { createApiClient } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// `/api/v1/scan/resolve` is permission-gated (`scan:resolve`) and tenant-scoped;
// the typed client must attach `Authorization: Bearer <accessToken>` via the
// shared `tokenStorage` flow (PR #119) so requests do not 401.
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type ResolveScanRequest =
  ApiComponents['schemas']['ResolveScanRequestDto'];
export type ResolveScanResponse =
  ApiComponents['schemas']['ResolveScanResponseDto'];

export interface ResolveScanError {
  message: string;
  status?: number;
}

export interface UseResolveScanState {
  loading: boolean;
  error: ResolveScanError | null;
  data: ResolveScanResponse | null;
}

export function useResolveScan(): UseResolveScanState & {
  resolve: (input: ResolveScanRequest) => Promise<ResolveScanResponse | null>;
  reset: () => void;
} {
  const [state, setState] = useState<UseResolveScanState>({
    loading: false,
    error: null,
    data: null,
  });

  const resolve = useCallback(
    async (input: ResolveScanRequest): Promise<ResolveScanResponse | null> => {
      setState({ loading: true, error: null, data: null });
      try {
        const { data, error } = await apiClient.POST('/api/v1/scan/resolve', {
          body: input,
        });
        if (error) {
          const apiErr = error as { message?: string };
          const message = apiErr.message ?? '扫码解析失败';
          setState({ loading: false, error: { message }, data: null });
          return null;
        }
        const resp = (data ?? null) as ResolveScanResponse | null;
        setState({ loading: false, error: null, data: resp });
        return resp;
      } catch (err) {
        const message = err instanceof Error ? err.message : '扫码解析失败';
        const status = (err as { status?: number } | undefined)?.status;
        setState({
          loading: false,
          error: { message, status },
          data: null,
        });
        return null;
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setState({ loading: false, error: null, data: null });
  }, []);

  return { ...state, resolve, reset };
}
